import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getIndustryPage } from '../api/industryApi';
import Loader from '../components/Loader';
import IndustrySection, { IndustryHero } from '../components/industry/IndustrySections';
import '../styles/industry-content.css';

export default function IndustryContent() {
  const { slug } = useParams();
  const [state, setState] = useState({ status: 'loading', page: null });
  const [attempt, setAttempt] = useState(0);
  const container = useRef(null);

  useEffect(() => {
    // Measure the existing fixed header without changing its markup or styling.
    const header = document.querySelector('header.header');
    if (!header) return;
    const update = () => container.current?.style.setProperty('--ip-header-height', `${header.getBoundingClientRect().height}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading', page: null });
    window.scrollTo(0, 0);
    getIndustryPage(slug, controller.signal).then(page => {
      if (!controller.signal.aborted) setState({ status: page?.hero ? 'ready' : 'missing', page });
    }).catch(error => {
      if (!controller.signal.aborted) setState({ status: 'error', page: null });
    });
    return () => controller.abort();
  }, [slug, attempt]);

  useEffect(() => {
    if (!state.page) return;
    const originalTitle = document.title;
    const existing = document.querySelector('meta[name="description"]');
    const meta = existing || document.createElement('meta');
    const originalDescription = meta.getAttribute('content');
    meta.name = 'description';
    document.title = state.page.seo?.title || `${state.page.name} | Clover Bay Technologies`;
    meta.content = state.page.seo?.description || state.page.hero?.description || '';
    if (!existing) document.head.appendChild(meta);
    return () => {
      document.title = originalTitle;
      if (!existing) meta.remove();
      else if (originalDescription === null) meta.removeAttribute('content');
      else meta.content = originalDescription;
    };
  }, [state.page]);

  return <div ref={container} className="industry-content" aria-busy={state.status === 'loading'}>
    {state.status === 'loading' ? <div role="status" aria-label="Loading"><Loader /></div> : state.status === 'ready' ? <>
      <IndustryHero hero={state.page.hero} name={state.page.name} detail={Boolean(slug)} />
      {state.page.sections?.map((section, i) => <IndustrySection key={section.id || i} section={section} />)}
    </> : <div className="ip-status" role="status" aria-live="polite">
      {state.status === 'error' ? <><h1>We couldn’t load this page.</h1><p>Please try again in a moment.</p><button className="ip-button" onClick={() => setAttempt(value => value + 1)}>Try again</button></>
        : <><h1>Page not available</h1><p>This industry page hasn’t been published yet, or the address is incorrect.</p><Link className="ip-button" to={slug ? '/industries' : '/'}>{slug ? 'Explore industries' : 'Back to home'}</Link></>}
    </div>}
  </div>;
}
